import { AmountSuperScript } from "components/design/AmountSuperScript";
import Card from "components/DesignSystem/Card/Card";
import { Button } from "components/DesignSystem/Button/Button";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { useGetTransaction } from "hooks/useGetTransaction";
import { Category } from "./TransactionInfo";
import { Divider } from "components/design/Divider";
import { SplitIcon } from "components/icons/SplitIcon";
import { useDispatch } from "react-redux";
import { openEditSplitTransactionModal } from "store/slices/splitTransaction";
import ConditionalToolTip from "components/design/conditionalToolTip";

export const SplitTransactionDetail = ({
  transactionId,
}: {
  transactionId: string;
}) => {
  const dispatch = useDispatch();
  const { data: transactionDetail } = useGetTransaction({
    transactionId,
  });
  const hasSplits = Boolean(transactionDetail?.split_transactions);

  const handleEdit = () => {
    if (transactionDetail) {
      dispatch(openEditSplitTransactionModal(transactionDetail));
    }
  };

  return (
    <Card.Root variant="secondary" borderRounded="lg">
      <Card.Header sticky={false}>
        <Card.Title>Categorisation & Classifications</Card.Title>
        <Button customType="link" size="small" onClick={handleEdit}>
          Edit
        </Button>
      </Card.Header>
      <Card.Body>
        <div>
          <Category transactionId={transactionId} />
        </div>
        <div className="t-relative t-mt-8">
          <Divider />
          <div className="t-absolute t-top-1/2 t-left-1/2 -t-translate-x-1/2 -t-translate-y-1/2 t-flex t-items-center t-gap-1.5 t-px-2 t-py-1 t-rounded-full t-bg-neutral-0">
            <SplitIcon />
            <span className="t-text-body-sm t-text-text-60">
              Split Transactions
            </span>
          </div>
        </div>
        <div className="t-grid t-grid-cols-4 t-gap-y-1 t-mt-8">
          {hasSplits && (
            <div className="t-grid t-grid-cols-4 t-col-span-4">
              <Label className="t-text-text-100 t-text-body-sm">
                Description
              </Label>
              <Label className="t-text-text-100 t-text-body-sm">Category</Label>

              <Label className="t-text-text-100 t-text-body-sm t-text-center">
                Class
              </Label>

              <Label className="t-text-text-100 t-text-body-sm t-text-right">
                Amount
              </Label>
            </div>
          )}

          {transactionDetail?.split_transactions?.map((transaction) => {
            const hasClassifications =
              transaction.classification_details &&
              transaction.classification_details.length > 0;
            const tooltipContent = hasClassifications && (
              <div className="t-flex t-flex-col t-gap-1">
                {transaction.classification_details?.map((classification) => (
                  <div
                    key={classification.uuid}
                    className="t-flex t-items-center"
                  >
                    <span className="t-text-neutral-30">
                      {classification.name}
                    </span>
                    : {classification.classes?.[0]?.name || "-"}
                  </div>
                ))}
              </div>
            );

            return (
              <div
                key={transaction.uuid}
                className="t-grid t-grid-cols-4 t-col-span-4"
              >
                <div className="t-text-text-100 t-text-body-sm">
                  {transaction.description}
                </div>

                <div className="t-text-text-100 t-text-body-sm">
                  {transaction.category?.name || "-"}
                </div>

                <ConditionalToolTip condition={tooltipContent}>
                  <span className="t-flex t-items-center t-text-center t-self-center t-justify-center t-justify-self-center t-w-6 t-h-6 t-rounded-full t-bg-neutral-0 t-text-caption t-text-neutral-50">
                    {transaction.classification_details?.length || "-"}
                  </span>
                </ConditionalToolTip>

                <div className="t-text-right t-text-body-sm">
                  <AmountSuperScript amount={transaction.amount} />
                </div>
              </div>
            );
          })}
        </div>
      </Card.Body>
    </Card.Root>
  );
};
